import React, { useState, useEffect } from 'react';
import './DonationPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookIcon, LabIcon, BuildingIcon, MoneyIcon } from '../components/Icons';
import useSEO from '../hooks/useSEO';

function DonationPage() {
  useSEO({
    title: "Faire un Don & Soutenir nos Projets Éducatifs | GSU Les Palmiers",
    description: "Soutenez activement la scolarisation des jeunes filles méritantes et l'équipement des ateliers professionnels du GSU Les Palmiers à Togba.",
    keywords: "don éducation Bénin, bourse scolarisation filles Togba, soutien école Les Palmiers, mécénat solidaire Calavi",
    canonicalUrl: "https://gsulespalmiers.bj/donation"
  });

  const [montant, setMontant] = useState('');
  const [montantPersonnalise, setMontantPersonnalise] = useState('');
  const [selectedProjet, setSelectedProjet] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    message: ''
  });

  useEffect(() => {
    const stored = localStorage.getItem('selected_projet_donation');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSelectedProjet(parsed);
      } catch (e) {
        console.error('Erreur parse projet donation:', e);
      }
    }
  }, []);

  const montantsSuggeres = [5000, 10000, 25000, 50000, 100000];

  const handleMontantClick = (amount) => {
    setMontant(amount);
    setMontantPersonnalise('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const montantFinal = montantPersonnalise || montant;
    
    if (!montantFinal) {
      alert('Veuillez sélectionner ou saisir un montant');
      return;
    }

    console.log('Don:', { ...formData, montant: montantFinal });
    alert(`Merci pour votre don de ${montantFinal.toLocaleString()} FCFA !\n\nVous serez redirigé vers la page de paiement.`);
  };

  return (
    <div className="donation-page">
      <Header />
      
      {/* Hero Section */}
      <div className="donation-hero">
        <div className="container">
          <h1>Soutenez l'Excellence Éducative</h1>
          <p>Votre générosité aide nos étudiants à réaliser leurs rêves</p>
        </div>
      </div>

      {selectedProjet && (
        <div style={{ background: '#eff6ff', borderBottom: '2px solid #bfdbfe', padding: '1rem 0' }}>
          <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <span style={{ fontSize: '0.85rem', color: '#1d4ed8', fontWeight: 600, textTransform: 'uppercase' }}>Projet sélectionné</span>
              <h3 style={{ margin: 0, color: '#1e3a8a', fontSize: '1.2rem' }}>🎯 {selectedProjet.titre}</h3>
            </div>
            <button 
              onClick={() => { setSelectedProjet(null); localStorage.removeItem('selected_projet_donation'); }}
              style={{ background: 'none', border: '1px solid #93c5fd', color: '#1e40af', padding: '0.3rem 0.8rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              Changer / Don général
            </button>
          </div>
        </div>
      )}

      {/* Bouton Retour */}
      <div className="back-button-section">
        <div className="container">
          <button 
            className="btn-back"
            onClick={() => {
              if (window.navigateTo) {
                window.navigateTo('projets');
              } else {
                window.history.back();
              }
            }}
            aria-label="Retour"
          >
            ← Retour
          </button>
        </div>
      </div>

      {/* Impact Section */}
      <div className="donation-impact">
        <div className="container">
          <h2>Votre Impact</h2>
          <div className="impact-grid">
            <div className="impact-card">
              <div className="impact-icon"><BookIcon size={48} color="#0078D7" /></div>
              <h3>Bourses d'Études</h3>
              <p>Aidez les étudiants méritants à poursuivre leurs études</p>
            </div>
            <div className="impact-card">
              <div className="impact-icon"><LabIcon size={48} color="#0078D7" /></div>
              <h3>Équipements</h3>
              <p>Modernisez nos laboratoires et ateliers techniques</p>
            </div>
            <div className="impact-card">
              <div className="impact-icon"><MoneyIcon size={48} color="#0078D7" /></div>
              <h3>Numérique</h3>
              <p>Développez l'accès aux outils informatiques</p>
            </div>
            <div className="impact-card">
              <div className="impact-icon"><BuildingIcon size={48} color="#0078D7" /></div>
              <h3>Infrastructure</h3>
              <p>Améliorez les conditions d'apprentissage</p>
            </div>
          </div>
        </div>
      </div>

      {/* Donation Form */}
      <div className="donation-form-section">
        <div className="container">
          <div className="donation-form-card">
            <h2>Faire un Don</h2>
            <p className="form-subtitle">Chaque contribution compte, peu importe le montant</p>

            <form onSubmit={handleSubmit}>
              {/* Montants suggérés */}
              <div className="montant-section">
                <label className="form-label">Choisissez un montant</label>
                <div className="montant-grid">
                  {montantsSuggeres.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      className={`montant-btn ${montant === amount ? 'active' : ''}`}
                      onClick={() => handleMontantClick(amount)}
                    >
                      {amount.toLocaleString()} FCFA
                    </button>
                  ))}
                </div>
              </div>

              {/* Montant personnalisé */}
              <div className="form-group">
                <label className="form-label">Ou saisissez un montant personnalisé</label>
                <div className="input-with-currency">
                  <input
                    type="number"
                    className="form-input"
                    placeholder="Montant"
                    value={montantPersonnalise}
                    onChange={(e) => {
                      setMontantPersonnalise(e.target.value);
                      setMontant('');
                    }}
                    min="1000"
                  />
                  <span className="currency">FCFA</span>
                </div>
              </div>

              {/* Informations du donateur */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Nom <span className="required">*</span></label>
                  <input
                    type="text"
                    name="nom"
                    className="form-input"
                    value={formData.nom}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Prénom <span className="required">*</span></label>
                  <input
                    type="text"
                    name="prenom"
                    className="form-input"
                    value={formData.prenom}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email <span className="required">*</span></label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Téléphone</label>
                  <input
                    type="tel"
                    name="telephone"
                    className="form-input"
                    value={formData.telephone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Message (optionnel)</label>
                <textarea
                  name="message"
                  className="form-textarea"
                  rows="4"
                  placeholder="Laissez-nous un message..."
                  value={formData.message}
                  onChange={handleInputChange}
                ></textarea>
              </div>

              <button type="submit" className="btn-submit-donation">
                Faire un don de {(montantPersonnalise || montant || 0).toLocaleString()} FCFA
              </button>
            </form>
          </div>

          {/* Informations supplémentaires */}
          <div className="donation-info">
            <div className="info-card">
              <h3>🔒 Paiement Sécurisé</h3>
              <p>Vos transactions sont protégées par un système de paiement sécurisé</p>
            </div>
            <div className="info-card">
              <h3>📧 Reçu Fiscal</h3>
              <p>Un reçu vous sera envoyé par email pour votre déclaration</p>
            </div>
            <div className="info-card">
              <h3>💚 Transparence</h3>
              <p>Nous publions régulièrement des rapports sur l'utilisation des dons</p>
            </div>
          </div>
        </div>
      </div>

      {/* Témoignages */}
      <div className="donation-testimonials">
        <div className="container">
          <h2>Ils Nous Soutiennent</h2>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <p className="testimonial-text">
                "Grâce aux dons, j'ai pu obtenir une bourse et poursuivre mes études en informatique. Aujourd'hui, je suis développeur."
              </p>
              <p className="testimonial-author">- Kofi A., Ancien étudiant</p>
            </div>
            <div className="testimonial-card">
              <p className="testimonial-text">
                "Contribuer à l'éducation des jeunes, c'est investir dans l'avenir du Bénin. Je suis fier de soutenir Les Palmiers."
              </p>
              <p className="testimonial-author">- Marie D., Donatrice</p>
            </div>
            <div className="testimonial-card">
              <p className="testimonial-text">
                "Les nouveaux équipements ont transformé nos ateliers. Nos étudiants apprennent avec du matériel moderne."
              </p>
              <p className="testimonial-author">- Prof. Mensah, Enseignant</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default DonationPage;
